/**
 * `useProfileStore` — Zustand slice over the End_User's `ProfileContext`.
 *
 * The `ProfileContext` is injected into every System_Prompt
 * (Requirement 1.1), so this slice is read on every outgoing turn and
 * mutated whenever the End_User edits a field in settings
 * (Requirement 1.4 / 13.2). Patches are persisted through the active
 * `ConversationStore` immediately — there is no debounce because saves
 * are cheap and the End_User expects "what I see is what's saved".
 *
 * On `hydrate` the slice reads the persisted profile from the store. If
 * none is present (first boot, or after `wipeAll` — see Requirement 1.5
 * / 16.10), the slice seeds the defaults from `seedDefaultProfile()`
 * AND writes them back to the store so the singleton is always
 * present in subsequent reads. This keeps Property 7 (Conversation_Store
 * matches a reference model) trivially satisfied: the store always
 * contains a profile after the first hydrate.
 */

import { create } from 'zustand';

import { seedDefaultProfile } from '../domain/types';
import type { ProfileContext } from '../domain/types';

import { getActiveConversationStore } from './storeContext';

/** Public slice surface. */
export interface ProfileState {
  /** The current `ProfileContext`. Always defined; never `null`. */
  profile: ProfileContext;
  /** `true` once `hydrate()` has resolved. */
  hydrated: boolean;
  /** Read the persisted profile (or seed defaults). */
  hydrate(): Promise<void>;
  /** Apply a partial patch and persist atomically. */
  patch(partial: Partial<ProfileContext>): Promise<void>;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  profile: seedDefaultProfile(),
  hydrated: false,

  async hydrate(): Promise<void> {
    const store = getActiveConversationStore();
    const persisted = await store.getProfile();
    if (persisted !== null) {
      // Migration: if displayName is empty, fill it from the new default
      const fresh = seedDefaultProfile();
      const migrated: ProfileContext = {
        ...persisted,
        displayName: persisted.displayName || fresh.displayName,
      };
      if (migrated.displayName !== persisted.displayName) {
        await store.setProfile(migrated);
      }
      set({ profile: migrated, hydrated: true });
      return;
    }
    // First boot or post-wipe: seed defaults and persist them so the
    // store always returns a profile on subsequent boots.
    const seeded = seedDefaultProfile();
    set({ profile: seeded, hydrated: true });
    await store.setProfile(seeded);
  },

  async patch(partial: Partial<ProfileContext>): Promise<void> {
    // Merge first, then persist. The pet field is an object literal,
    // so the spread merge correctly replaces it when included in the
    // patch and leaves it untouched otherwise.
    const next: ProfileContext = { ...get().profile, ...partial };
    set({ profile: next });
    await getActiveConversationStore().setProfile(next);
  },
}));
